import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getActiveStep } from '../../../../../../redux/toolsSlice';
import { updateStep } from '../../../../../../redux/stepsSlice';
import { STANDARD_ATTRIBUTES } from './tokenUtils';

const OPERATORS = [
  { value: 'is', label: 'is' },
  { value: 'is_not', label: 'is not' },
  { value: 'contains', label: 'contains' },
  { value: 'is_one_of', label: 'is one of' },
  { value: 'exists', label: 'exists' },
];

const OP_TEXT = {
  is: 'is', is_not: 'is not', contains: 'contains', is_one_of: 'is one of', exists: 'is set',
};

function labelOf(key) {
  const f = STANDARD_ATTRIBUTES.find((a) => a.key === key);
  return f ? f.label : key;
}

function buildSummary(rule) {
  const conds = (rule.conditions || []).filter((c) => c.attribute);
  if (conds.length === 0) return 'Shown to everyone.';
  const parts = conds.map((c) => {
    const op = OP_TEXT[c.operator] || c.operator;
    const val = c.operator === 'exists' ? '' : ` "${c.value || '…'}"`;
    return `${labelOf(c.attribute)} ${op}${val}`;
  });
  const join = rule.match === 'any' ? ' or ' : ' and ';
  const verb = rule.action === 'skip' ? 'Skipped' : 'Shown';
  return `${verb} when ${parts.join(join)}.${rule.action === 'skip' ? " Skipped steps don't count against progress." : ''}`;
}

// BL-081 — display-rule builder shown when a step is selected. Saves the rule spec
// onto the step under `display_rule` (the exact shape the backend RuleEvaluator /
// ContentPersonalizer read).
function StepRuleBuilder() {
  const dispatch = useDispatch();
  const activeStep = useSelector(getActiveStep);

  if (!activeStep || !activeStep.id) return null;

  const rule = activeStep.display_rule && typeof activeStep.display_rule === 'object'
    ? activeStep.display_rule
    : { action: 'show', match: 'all', conditions: [] };

  const save = (next) => dispatch(updateStep({ ...activeStep, display_rule: next }));
  const setAction = (action) => save({ ...rule, action });
  const setMatch = (match) => save({ ...rule, match });
  const setCond = (i, patch) => {
    const c = [...(rule.conditions || [])];
    c[i] = { ...c[i], ...patch };
    save({ ...rule, conditions: c });
  };
  const addCond = () => save({ ...rule, conditions: [...(rule.conditions || []), { attribute: 'role', operator: 'is', value: '' }] });
  const rmCond = (i) => {
    const c = [...(rule.conditions || [])];
    c.splice(i, 1);
    save({ ...rule, conditions: c });
  };

  return (
    <div className="un-rulebox">
      <p className="un-rulebox__lab">Display rules</p>
      <div className="un-seg">
        <button type="button" className={`un-seg__s${rule.action !== 'skip' ? ' on' : ''}`} onClick={() => setAction('show')}>Show when…</button>
        <button type="button" className={`un-seg__s${rule.action === 'skip' ? ' on' : ''}`} onClick={() => setAction('skip')}>Skip when…</button>
      </div>

      {(rule.conditions || []).map((c, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={i}>
          {i > 0 && (
            <div className="un-andor">
              <button type="button" className={rule.match !== 'any' ? 'on' : ''} onClick={() => setMatch('all')}>AND</button>
              <button type="button" className={rule.match === 'any' ? 'on' : ''} onClick={() => setMatch('any')}>OR</button>
            </div>
          )}
          <div className="un-cond">
            <select value={c.attribute} onChange={(e) => setCond(i, { attribute: e.target.value })}>
              {STANDARD_ATTRIBUTES.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
            </select>
            <select value={c.operator} onChange={(e) => setCond(i, { operator: e.target.value })}>
              {OPERATORS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {c.operator !== 'exists' && (
              <input value={c.value || ''} placeholder="value" onChange={(e) => setCond(i, { value: e.target.value })} />
            )}
            <button type="button" className="un-cond__rm" onClick={() => rmCond(i)} aria-label="Remove condition">&times;</button>
          </div>
        </React.Fragment>
      ))}

      <button type="button" className="un-addcond" onClick={addCond}>&#65291; Add condition</button>
      <div className="un-rulesummary">{buildSummary(rule)}</div>
    </div>
  );
}

export default StepRuleBuilder;
